import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { MainLayout } from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Ad } from "@/types/ad";
import { AdNumberLink, NumberRecord } from "@/types/ad-number";
import { AdFormDialog } from "@/components/ads/AdFormDialog";
import { AdsTable } from "@/components/ads/AdsTable";
import { BuyAdNumberDialog } from "@/components/ads/BuyAdNumberDialog";
import { FixCredentialsButton } from "@/components/debug/FixCredentialsButton";
import { ForwardingRulesEditor, type ForwardingRules } from "@/components/campaigns/ForwardingRulesEditor";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function CampaignDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const qc = useQueryClient();

  // SEO
  useEffect(() => {
    document.title = `Campaign Details | Calie.ai`;
    const desc = 'Manage ads and tracking numbers for your campaign.';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement('meta'); meta.setAttribute('name', 'description'); document.head.appendChild(meta); }
    meta.setAttribute('content', desc);
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = window.location.origin + `/campaigns/${id}`;
  }, [id]);

  const { data: campaign, isLoading: loadingCampaign } = useQuery({
    queryKey: ['campaign', id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calie_campaigns')
        .select('*, forwarding_rules')
        .eq('campaign_id', id)
        .maybeSingle();
      if (error) throw error;
      return data;
    }
  });

  const { data: ads, isLoading: loadingAds } = useQuery({
    queryKey: ['ads', id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calie_ads')
        .select('*')
        .eq('campaign_id', id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as Ad[];
    }
  });

  const { data: adLinks } = useQuery({
    queryKey: ['ad-number-links', id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calie_ad_numbers')
        .select('*')
        .in('ad_id', (ads || []).map(a => a.id));
      if (error) throw error;
      return (data || []) as AdNumberLink[];
    }
  });

  const { data: numbers } = useQuery({
    queryKey: ['numbers-by-id', adLinks?.map(l => l.number_id).join(',')],
    enabled: (adLinks || []).length > 0,
    queryFn: async () => {
      const ids = (adLinks || []).map(l => l.number_id);
      const { data, error } = await supabase
        .from('calie_numbers')
        .select('number_id,e164')
        .in('number_id', ids);
      if (error) throw error;
      return (data || []) as NumberRecord[];
    }
  });

  const numbersByAd = useMemo(() => {
    const byNumberId = new Map((numbers || []).map(n => [n.number_id, n.e164]));
    const map: Record<string, string | undefined> = {};
    (adLinks || []).forEach(l => { map[l.ad_id] = byNumberId.get(l.number_id); });
    return map;
  }, [numbers, adLinks]);

  const [formOpen, setFormOpen] = useState(false);
  const [buyOpen, setBuyOpen] = useState<null | Ad>(null);

  const toggleStatus = useMutation({
    mutationFn: async (ad: Ad) => {
      const next = ad.status === 'active' ? 'paused' : 'active';
      const { error } = await supabase
        .from('calie_ads')
        .update({ status: next })
        .eq('id', ad.id);
      if (error) throw error;
      return next;
    },
    onSuccess: () => {
      toast({ title: 'Ad status updated' });
      qc.invalidateQueries({ queryKey: ['ads', id] });
    },
    onError: (e: any) => {
      toast({ title: 'Failed to update ad', description: e.message, variant: 'destructive' });
    }
  });

  const deleteAd = useMutation({
    mutationFn: async (ad: Ad) => {
      const { error } = await supabase
        .from('calie_ads')
        .delete()
        .eq('id', ad.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: 'Ad deleted' });
      qc.invalidateQueries({ queryKey: ['ads', id] });
      qc.invalidateQueries({ queryKey: ['ad-number-links', id] });
    },
    onError: (e: any) => {
      toast({ title: 'Failed to delete ad', description: e.message, variant: 'destructive' });
    }
  });
  return (
    <MainLayout>
      <main className="space-y-6">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Campaign</h1>
            <p className="text-sm text-muted-foreground">Create ads and assign tracking numbers.</p>
          </div>
          <div className="flex gap-2">
            <FixCredentialsButton />
            <Button variant="secondary" onClick={() => navigate('/campaigns')}>Back</Button>
            <Button onClick={() => setFormOpen(true)} disabled={!campaign}>New Ad</Button>
          </div>
        </header>

        {loadingCampaign || loadingAds ? (
          <div>Loading…</div>
        ) : (
          <Tabs defaultValue="ads" className="space-y-4">
            <TabsList>
              <TabsTrigger value="ads">Ads & Numbers</TabsTrigger>
              <TabsTrigger value="forwarding">Call Forwarding</TabsTrigger>
            </TabsList>
            
            <TabsContent value="ads">
              <AdsTable 
                ads={ads || []}
                numbersByAd={numbersByAd}
                onBuyNumber={(ad) => setBuyOpen(ad)}
                onToggleStatus={(ad) => toggleStatus.mutate(ad)}
                onDeleteAd={(ad) => {
                  if (window.confirm('Delete this ad? This cannot be undone.')) {
                    deleteAd.mutate(ad);
                  }
                }}
              />
            </TabsContent>
            
            <TabsContent value="forwarding">
              <ForwardingRulesEditor
                campaignId={id!}
                currentRules={campaign?.forwarding_rules as ForwardingRules | undefined}
                simpleForwardingNumber={campaign?.forwarding_number}
              />
            </TabsContent>
          </Tabs>
        )}

        <AdFormDialog 
          open={formOpen}
          onOpenChange={setFormOpen}
          campaignId={id!}
          tenantId={campaign?.tenant_id}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['ads', id] });
          }}
        />

        <BuyAdNumberDialog 
          ad={buyOpen}
          open={!!buyOpen}
          onOpenChange={(v) => !v && setBuyOpen(null)}
          onPurchased={() => {
            toast({ title: 'Number purchased' });
            qc.invalidateQueries({ queryKey: ['ad-number-links', id] });
          }}
        />
      </main>
    </MainLayout>
  );
}
